import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../Icon';
import ServiceGlyph from '../ServiceGlyph';
import SituationGlyph from '../SituationGlyph';
import Eyebrow from '../page/Eyebrow';
import { tie } from '../page/typography';

// Персонал із документами. Окремого малюнка для цього на сайті немає, тож
// він тут, тими самими лініями, що й ServiceGlyph.
const StaffGlyph = ({ className }) => (
  <svg
    viewBox="0 0 64 64"
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="28" cy="18" r="9" />
    <path d="M10 56v-5c0-9 8-15 18-15 4 0 7.5.9 10.5 2.6" />
    <rect x="38" y="42" width="18" height="14" rx="2" />
    <path d="M42 48h10M42 52h6" />
  </svg>
);

/*
 * Малюнки ланок — тими самими лініями, що й на картках напрямків вище, а не
 * піктограмами Font Awesome з переліку в content/pages.js: інакше на одній
 * сторінці стояли б два набори іконок різної ваги. Там, де ланку закриває
 * конкретний напрямок, малюнок той самий, що на його картці, — око саме
 * зводить «Реєстрація в ЕСОЗ» тут і картку ЕСОЗ вище.
 */
const CHAIN_GLYPHS = [
  (cls) => <ServiceGlyph index={0} className={cls} />,
  (cls) => <SituationGlyph index={1} className={cls} />,
  (cls) => <StaffGlyph className={cls} />,
  (cls) => <ServiceGlyph index={1} className={cls} />,
  (cls) => <ServiceGlyph index={3} className={cls} />,
  (cls) => <ServiceGlyph index={4} className={cls} />
];

const PATHS = [
  {
    glyph: 1,
    title: 'Відкриваєте клініку з нуля?',
    text: 'Тоді потрібні не окремі послуги, а послідовність: майже всі напрямки й у певному порядку. Ми розклали шлях від ідеї до першого пацієнта на шість етапів — з переліком того, що робиться на кожному.',
    to: '/plan-zapusku-kliniky',
    label: 'План запуску клініки'
  },
  {
    glyph: 5,
    title: 'Клініка вже працює?',
    text: 'Зазвичай ідеться про один-два напрямки, і кожен можна замовити окремо. Якщо не певні, який саме, почніть із ситуації: відмовили в ліцензії, минув строк подання Відомостей, прийшла перевірка.',
    to: '/z-chogo-pochaty',
    label: 'Типові ситуації'
  }
];

/**
 * «Один ланцюг»: що має бути в закладі до першого пацієнта, чому ці пункти
 * пов’язані — і два шляхи далі.
 *
 * Раніше це був вступ над картками: три абзаци й поруч той самий перелік
 * карткою з номерами 01–06. Перший абзац перелічував рівно те, що стояло в
 * картці, тож тут лишився тільки перелік. Третій («з нуля — майже всі,
 * чинній клініці — один-два») і окремий сірий блок «Відкриваєте клініку з
 * нуля?» казали одне й те саме — тепер це дві плитки-розвилки.
 *
 * Стоїть під картками, а не над ними: по напрямок сюди приходять, а пояснення
 * потрібне тому, хто серед шести карток свого не впізнав.
 */
const ChainPanel = ({ checklist, text, className = '' }) => {
  const items = checklist.items || [];
  // Дві колонки по три ланки — лише для шести пунктів: сітка планшета
  // розрахована рівно на три рядки.
  const split = items.length === 6;
  return (
    // Від sm панель — сама секція, і розвилки стоять у ній під рискою. На
    // телефоні ж плитка розвилки в панелі втрачала два поля поспіль, і текст
    // ішов по три-чотири слова в рядку, тож там підкладка лише під ланцюгом,
    // а розвилки — окремим рядом під нею, на всю ширину, як картки напрямків.
    <section
      aria-labelledby="chain-title"
      className={`sm:rounded-2xl sm:border sm:border-slate-100 sm:bg-slate-50 sm:p-8 lg:p-10 ${className}`}
    >
      <div className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-6 sm:rounded-none sm:border-0 sm:bg-transparent sm:p-0">
        {/* Нижче lg: заголовок, перелік, абзац — «Кожен із цих пунктів»
            читається вже після пунктів. Від lg перелік стоїть праворуч на
            всі рядки сітки, а заголовок з абзацом — ліворуч між двома
            порожніми 1fr: перелік вищий за текст, і так текст стоїть
            посередині, а не обривається на сотню пікселів вище за перелік. */}
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,24rem)] lg:grid-rows-[1fr_auto_auto_1fr] lg:gap-x-16 lg:gap-y-0">
          <div className="lg:col-start-1 lg:row-start-2">
            <Eyebrow className="mb-3">Один ланцюг</Eyebrow>
            <h2 id="chain-title" className="text-2xl lg:text-3xl font-extrabold font-display text-slate-900 leading-tight text-balance">
              {tie(checklist.title)}
            </h2>
          </div>

          {/* На планшеті перелік на всю ширину в одну колонку — це короткі
              підписи з порожньою правою половиною картки, тож там він стає
              двома колонками по три ланки, і лінія рветься на кінці першої. */}
          <ul className={`lg:col-start-2 lg:row-start-1 lg:row-span-4 lg:self-center list-none rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 ${split ? 'sm:grid sm:grid-cols-2 sm:grid-flow-col sm:grid-rows-[repeat(3,auto)] sm:gap-x-8 lg:block' : ''}`}>
            {items.map((item, i) => {
              const glyph = CHAIN_GLYPHS[i];
              const last = i === items.length - 1;
              // Кінець першої колонки на планшеті: там ланцюг переходить у другу.
              const columnEnd = split && i === items.length / 2 - 1;
              return (
                <li key={item.label} className={`relative flex items-center gap-4 ${last ? '' : columnEnd ? 'pb-3 sm:pb-0 lg:pb-3' : 'pb-3'}`}>
                  {/* Лінія від ланки до ланки: це ланцюг, а не довільний перелік. */}
                  {!last && (
                    <span className={`absolute left-[1.375rem] top-11 bottom-0 w-px bg-teal-200 ${columnEnd ? 'sm:hidden lg:block' : ''}`} aria-hidden="true"></span>
                  )}
                  <span className="relative flex-shrink-0 w-11 h-11 rounded-xl border border-teal-100 bg-teal-50 text-teal-700 flex items-center justify-center" aria-hidden="true">
                    {glyph ? glyph('w-6 h-6') : <Icon name={item.icon} />}
                  </span>
                  <span className="font-semibold text-slate-800 leading-snug">{tie(item.label)}</span>
                </li>
              );
            })}
          </ul>

          <p className="lg:col-start-1 lg:row-start-3 lg:mt-6 text-base lg:text-lg leading-relaxed text-slate-600">
            {tie(text)}
          </p>
        </div>
      </div>

      <div className="mt-5 sm:mt-8 sm:pt-8 lg:mt-10 sm:border-t sm:border-slate-200 grid gap-5 lg:grid-cols-2 lg:gap-6">
        {PATHS.map((path) => (
          // Сітка, а не два стовпчики: на телефоні малюнок стоїть поруч із
          // заголовком, а текст під ними йде на всю ширину плитки; від sm
          // текст і посилання — у другій колонці, під заголовком. Рядок
          // 1fr тримає посилання внизу, коли сусідня плитка вища.
          <div
            key={path.to}
            className="group relative grid grid-cols-[auto_minmax(0,1fr)] grid-rows-[auto_1fr_auto] gap-x-4 rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 lg:p-7 transition-[border-color,box-shadow] duration-200 hover:border-teal-300 hover:shadow-md"
          >
            {/* Плашка та сама, що в ланок ланцюга вище: голий малюнок
                поруч із ними виглядав недомальованим. */}
            <span className="w-11 h-11 rounded-xl border border-teal-100 bg-teal-50 text-teal-700 flex items-center justify-center" aria-hidden="true">
              <SituationGlyph index={path.glyph} className="w-7 h-7" />
            </span>
            <h3 className="self-center text-lg lg:text-xl font-bold font-display text-slate-900 leading-snug text-balance">{tie(path.title)}</h3>
            <p className="col-span-2 sm:col-span-1 sm:col-start-2 mt-3 sm:mt-2 text-slate-600 leading-relaxed">{tie(path.text)}</p>
            {/* Посилання розтягнуте на всю плитку: влучити легко, а диктор
                чує коротку назву, куди воно веде. */}
            <Link
              to={path.to}
              className="col-span-2 sm:col-span-1 sm:col-start-2 justify-self-start pt-4 sm:pt-5 inline-flex items-center gap-2 font-semibold text-teal-700 group-hover:text-teal-800 transition-colors duration-200 focus:outline-none after:absolute after:inset-0 after:rounded-2xl focus-visible:after:ring-2 focus-visible:after:ring-teal-700 focus-visible:after:ring-offset-2"
            >
              {path.label}
              <Icon name="fa-arrow-right" className="transition-transform duration-200 motion-reduce:transition-none group-hover:translate-x-1" />
            </Link>
          </div>
        ))}
      </div>
    </section>
  );
};

export default ChainPanel;
